import { useNavigate, useLocation } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { toast } from "sonner";
import API from "../lib/api";
import auditLogo from "../assets/zentis-logo.png";
import AuditAssignment from "./AuditAssignment";
import AuditModeSelector from "./AuditModeSelector";
import {
  FileText, Upload, Target, Folder, Search,
  FileCheck, CheckSquare, FileOutput, Settings, Activity, Plus,
  Database, Shield, LogOut, Users, RefreshCw, UserCircle, PlugZap, Eye,
  ClipboardCheck, Check,
} from "lucide-react";

// Strip HTML tags and script content from any string (safety measure)
const stripHtml = (str) => str ? str.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, '').trim() : '';

const NAV_ITEMS = [
  { phase: 1, label: "Context", icon: FileText, path: "/phase/1" },
  { phase: 2, label: "Documents", icon: Upload, path: "/phase/2" },
  { phase: 3, label: "Controls", icon: Shield, path: "/phase/3" },
  { phase: 4, label: "Scope", icon: Target, path: "/phase/4" },
  { phase: 5, label: "Artifacts", icon: Folder, path: "/phase/5" },
  { phase: 7, label: "Evidence", icon: Search, path: "/phase/7" },
  { phase: 7.5, label: "Control Testing", icon: ClipboardCheck, path: "/phase/7-testing" },
  { phase: 8, label: "Findings", icon: FileCheck, path: "/phase/8" },
  { phase: 9, label: "Classification", icon: CheckSquare, path: "/phase/9" },
  { phase: 10, label: "Reports", icon: FileOutput, path: "/phase/10" },
];

const UTIL_ITEMS = [
  { label: "Integrations", icon: PlugZap, path: "/integrations" },
  { label: "Model Config", icon: Settings, path: "/config", permissions: ["model:config"] },
  { label: "User Management", icon: Users, path: "/users", permissions: ["users:manage"] },
  { label: "Audit Logs", icon: Activity, path: "/logs", permissions: ["logs:view"] },
];

export default function Sidebar({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditRun, auditRuns, currentPhase, loadRun, fetchRuns, newAuditDialogOpen, setNewAuditDialogOpen } = useAudit();
  const { user: authUser, logout, hasAnyPermission } = useAuth();
  const canCreateRuns = hasAnyPermission("run:create") || authUser?.permissions?.includes("run:create") || authUser?.role === "admin" || authUser?.role === "super_admin";
  const canAssignRuns = hasAnyPermission("run:assign") || authUser?.permissions?.includes("run:assign") || authUser?.role === "admin" || authUser?.role === "super_admin";
  const [newRunName, setNewRunName] = useState("");
  const [createStep, setCreateStep] = useState("name"); // "name" or "mode"
  const [creatingRun, setCreatingRun] = useState(false);
  const TOTAL_PHASES = 10;
  const completedPhases = Array.isArray(auditRun?.phases_completed) ? auditRun.phases_completed : [];
  const completedSet = new Set(completedPhases);
  // Progress is measured against the 10 real audit phases. The final phase
  // (Reports) counts as complete once the run has reached it, since nothing
  // advances past it.
  const completedJourneyCount = Array.from({ length: TOTAL_PHASES }, (_, i) => i + 1)
    .filter((p) => completedSet.has(p) || (p === TOTAL_PHASES && (currentPhase ?? 1) >= TOTAL_PHASES))
    .length;
  const journeyProgress = TOTAL_PHASES
    ? Math.max(0, Math.min(100, Math.round((completedJourneyCount / TOTAL_PHASES) * 100)))
    : 0;

  const handleNavClick = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleCreate = async () => {
    if (!newRunName.trim()) return;
    setCreateStep("mode");
  };

  const handleModeSelect = async (modeConfig) => {
    if (creatingRun) return;
    try {
      setCreatingRun(true);
      const { data } = await API.post("/audit-runs", {
        name: newRunName.trim(),
        audit_mode: modeConfig.audit_mode,
        team_template: modeConfig.team_template,
        custom_team_config: modeConfig.custom_team_config || null
      });
      setNewRunName("");
      setNewAuditDialogOpen(false);
      setCreateStep("name");
      await loadRun(data.id);
      await fetchRuns();
      handleNavClick("/phase/1");
      toast.success(
        modeConfig.audit_mode === "team"
          ? modeConfig.custom_team_config?.agents?.length
            ? "Custom audit team created!"
            : `Audit team created with ${modeConfig.team_template} template!`
          : "Audit run created"
      );
    } catch (e) {
      toast.error("Failed to create audit run");
    } finally {
      setCreatingRun(false);
    }
  };

  const handleDialogClose = (open) => {
    setNewAuditDialogOpen(open);
    if (!open) {
      setCreateStep("name");
      setNewRunName("");
      setCreatingRun(false);
    }
  };

  const handleSeedDemo = async () => {
    try {
      setNewAuditDialogOpen(false);
      const { data } = await API.post("/demo/seed");
      await loadRun(data.audit_run_id);
      await fetchRuns();
      handleNavClick("/phase/8");
      toast.success("Demo data loaded");
    } catch (e) {
      toast.error("Failed to seed demo data");
    }
  };

  const isPhaseAccessible = (phase) => {
    if (!auditRun) return phase === 1;
    const completed = auditRun.phases_completed || [];
    return phase === 1 || completed.includes(phase - 1) || phase <= currentPhase;
  };

  return (
    <div className="audit-sidebar h-full flex flex-col bg-card/90 text-card-foreground select-none" data-testid="sidebar">
      {/* Brand Header */}
      <div className="p-4 sm:p-5 flex flex-col items-start gap-2 border-b border-border/60">
        {/* Main Zentis Logo from zentis-logo.png */}
        <div className="flex items-center">
          <img
            src={auditLogo}
            alt="Zentis Logo"
            className="h-7 w-auto object-contain"
          />
        </div>

        {/* AuditOS and Compliance Hub Underneath */}
        <div className="pt-0.5">
          <span className="text-base font-black tracking-tight text-foreground font-heading block leading-none">
            AuditOS
          </span>
          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 tracking-wider uppercase block mt-1">
            Compliance Hub
          </span>
        </div>
      </div>

      {/* Audit Run Actions & Progress */}
      <div className="p-4 space-y-2.5 bg-muted/40 border-b border-border/60">
        {/* PRIMARY CTA: NEW AUDIT */}
        {canCreateRuns && (
          <Dialog open={newAuditDialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="w-full h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold shadow-md shadow-emerald-600/20 border-0 transition-all flex items-center justify-center gap-2"
                data-testid="new-audit-btn"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span>New Audit Run</span>
              </Button>
            </DialogTrigger>
            <DialogContent className={createStep === "mode" ? "max-w-xl max-h-[90vh] overflow-hidden rounded-2xl" : "rounded-2xl"}>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">
                  {createStep === "name" ? "New Audit Run" : "Select Audit Mode"}
                </DialogTitle>
              </DialogHeader>

              {createStep === "name" ? (
                <div className="space-y-3 pt-2">
                  <Input
                    placeholder="Audit run name..."
                    value={newRunName}
                    onChange={e => setNewRunName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    autoFocus
                    data-testid="new-run-name-input"
                    className="h-10 text-sm rounded-xl"
                  />
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleCreate} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-500" data-testid="create-run-btn" disabled={!newRunName.trim()}>
                      Continue
                    </Button>
                    <Button variant="outline" onClick={handleSeedDemo} className="rounded-xl" data-testid="seed-demo-btn">
                      <Database className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> Demo Data
                    </Button>
                  </div>
                </div>
              ) : (
                <AuditModeSelector
                  onSelect={handleModeSelect}
                  onCancel={() => setCreateStep("name")}
                  submitting={creatingRun}
                />
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* SUB-ACTIONS ROW: REFRESH & ASSIGN */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 rounded-xl border-border bg-card hover:bg-accent text-[11px] font-semibold text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"
            onClick={fetchRuns}
            data-testid="refresh-runs-list"
            title="Refresh audit runs"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Refresh</span>
          </Button>

          {canAssignRuns && auditRun && <AuditAssignment />}
        </div>

        {auditRun && (
          <div className="rounded-xl border border-border/80 bg-card p-3 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Overall Progress
              </span>
              <span className="font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400">{journeyProgress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                style={{ width: `${journeyProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{completedJourneyCount}/{TOTAL_PHASES} Phases</span>
              <span className="truncate max-w-[110px]" title={auditRun.name}>
                {currentPhase >= 10 ? "Completed" : `Phase ${Math.min(10, (currentPhase || 1))}`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Links */}
      <ScrollArea className="flex-1 px-3 py-3">
        <div className="space-y-1">
          <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Audit Phases</p>
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path;
            const accessible = isPhaseAccessible(item.phase);
            // Control Testing (7.5) is a sub-stage of Evidence: it counts as
            // complete once phase 7 has been completed (moving past 7/7.5).
            const completed = item.phase === 7.5
              ? completedSet.has(7)
              : completedSet.has(item.phase);

            return (
              <button
                key={item.path}
                onClick={() => accessible && handleNavClick(item.path)}
                disabled={!accessible}
                data-testid={`nav-phase-${item.phase}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium transition-all group relative
                  ${active 
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                  }
                  ${!accessible ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
                )}
                <item.icon className={`h-4 w-4 shrink-0 transition-transform group-hover:scale-110 ${active ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground group-hover:text-foreground'}`} />
                <span className="truncate flex-1 text-left">{item.label}</span>
                {completed ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                ) : (
                  <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">›</span>
                )}
              </button>
            );
          })}
        </div>

        <Separator className="my-3 bg-border/60" />

        <div className="space-y-1">
          <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Connections & Admin</p>
          {UTIL_ITEMS.filter(item => !item.permissions || hasAnyPermission(...item.permissions)).map(item => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium transition-all group relative
                  ${active 
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                  }
                `}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-emerald-500" />
                )}
                <item.icon className={`h-4 w-4 shrink-0 transition-transform group-hover:scale-110 ${active ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground group-hover:text-foreground'}`} />
                <span className="truncate flex-1 text-left">{item.label}</span>
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {/* User Footer */}
      <div className="p-3 border-t border-border/60 bg-muted/20">
        {authUser && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex-1 flex items-center gap-2.5 rounded-xl p-2 text-left hover:bg-accent transition-colors min-w-0"
              onClick={() => handleNavClick("/profile")}
              data-testid="open-profile-btn"
            >
              <div className="h-8 w-8 rounded-lg bg-gradient-to-tr from-emerald-600 to-teal-600 flex items-center justify-center text-white font-bold text-xs shrink-0 shadow-sm">
                {stripHtml(authUser.name)?.replace(/[^a-zA-Z0-9]/g, '')?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-foreground truncate">{stripHtml(authUser.name)}</p>
                <p className="text-[10px] text-muted-foreground truncate capitalize">
                  {String(authUser.role || 'Auditor').replace(/_/g, ' ')}
                </p>
              </div>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
              onClick={() => { logout(); navigate('/login'); }}
              data-testid="logout-btn"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
